import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Plus, Check, Loader } from "lucide-react";
import { Input } from "./ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { FieldLabel } from "./ui/field";
import { ICompasAIChatAction } from "@/types/compass.types";
import { useChatMessagesChain } from "@/hooks/useChatMessagesChain";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import type { OneClickAutomationFormValues as FormValues } from "@/schemas/automation.schemas";

export const CategorySection: React.FC<{
  control: ReturnType<typeof useForm<FormValues>>["control"];
  isCategoriesLoading: boolean;
  categories: { _id: string; name: string; parent_id?: string | null }[] | undefined;
  onCreateCategory: () => void;
}> = ({ control, isCategoriesLoading, categories, onCreateCategory }) => (
  <div className="space-y-1 flex-shrink-0">
    <div className="flex items-center justify-between">
      <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">Category</FieldLabel>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              className="size-6"
              size={"icon-sm"}
              onClick={(e) => {
                e.stopPropagation();
                onCreateCategory();
              }}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-[10px]">Create new category</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>

    <Controller
      name="categoryId"
      control={control}
      render={({ field }) => (
        <Select onValueChange={field.onChange} value={field.value}>
          <SelectTrigger className="w-full h-8 text-xs">
            <SelectValue placeholder="Select a Category" />
          </SelectTrigger>
          <SelectContent>
            {isCategoriesLoading ? (
              <div className="p-2 text-xs text-center">Loading...</div>
            ) : (
              categories
                ?.filter((cat) => cat.parent_id == null)
                .map((category) => (
                  <SelectItem key={category._id} value={category._id} className="text-xs">
                    {category.name}
                  </SelectItem>
                ))
            )}
          </SelectContent>
        </Select>
      )}
    />
  </div>
);

export const ActionsListSection: React.FC<{
  isAnonymizing: boolean;
  anonymizedActions: ICompasAIChatAction[];
}> = ({ isAnonymizing, anonymizedActions }) => (
  <div className="space-y-1 flex-1 flex flex-col min-h-0">
    <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">Action(s)</FieldLabel>
    {isAnonymizing ? (
      <div className="flex flex-col items-center justify-center flex-1 border rounded-md border-dashed bg-muted py-8">
        <Loader className="animate-spin text-primary-400 mb-2" />
        <span className="text-xs text-neutral-500">Anonymizing actions...</span>
      </div>
    ) : (
      <div className="flex-1 border rounded-md bg-muted min-h-[120px] overflow-y-auto">
        <div className="p-2 space-y-2">
          {anonymizedActions.map((action, idx) => (
            <div key={idx} className="bg-card p-2 rounded border text-xs">
              <div className="font-medium mb-0.5">
                {action.heading ?? action.label ?? `Action ${idx + 1}`}
              </div>
              <div className="text-neutral-500 line-clamp-2 font-normal">{action.prompt}</div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export const GroupsSection: React.FC<{
  searchGroup: string;
  onSearchChange: (value: string) => void;
  isGroupsLoading: boolean;
  groupsData: { data: { id: string; name: string }[] } | undefined;
  selectedGroups: string[];
  onToggleGroup: (groupId: string) => void;
}> = ({
  searchGroup,
  onSearchChange,
  isGroupsLoading,
  groupsData,
  selectedGroups,
  onToggleGroup,
}) => (
  <div className="space-y-1 flex-shrink-0">
    <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
      Search Group to share
    </FieldLabel>
    <Input
      placeholder="Enter group name"
      className="h-8 text-xs mb-2"
      value={searchGroup}
      onChange={(e) => onSearchChange(e.target.value)}
    />
    <div className="border rounded-md">
      <div className="p-2 flex flex-wrap gap-2">
        {isGroupsLoading ? (
          <p className="text-xs text-neutral-400">Loading groups...</p>
        ) : (
          groupsData?.data
            .filter((group) => group.name.toLowerCase().includes(searchGroup.toLowerCase()))
            .map((group) => {
              const isSelected = selectedGroups?.includes(group.id);
              return (
                <Badge
                  key={group.id}
                  variant="outline"
                  onClick={() => onToggleGroup(group.id)}
                  className={cn(
                    "rounded-md text-neutral-500 cursor-pointer text-xs h-6 px-2 font-normal",
                    isSelected && "bg-primary-300 text-neutral-white border-primary-300"
                  )}
                >
                  {isSelected && <Check className="w-3 h-3 mr-1" />}
                  {group.name}
                </Badge>
              );
            })
        )}
      </div>
    </div>
  </div>
);

export async function anonymizeActions(
  actions: ICompasAIChatAction[],
  mutateAsync: (input: {
    prompt: string;
    preserve_context: boolean;
  }) => Promise<{ anonymized_prompt?: string }>,
  setAnonymizedActions: (actions: ICompasAIChatAction[]) => void,
  setIsAnonymizing: (v: boolean) => void
) {
  setIsAnonymizing(true);
  try {
    const promises = actions.map((action) =>
      mutateAsync({ prompt: action.prompt, preserve_context: true })
    );
    const results = await Promise.all(promises);
    const newActions = actions.map((action, index) => ({
      ...action,
      prompt: results[index]?.anonymized_prompt ?? action.prompt,
    }));
    setAnonymizedActions(newActions);
  } catch (error) {
    console.error("Failed to anonymize actions:", error);
    toast.error("Failed to anonymize actions");
    setAnonymizedActions(actions);
  } finally {
    setIsAnonymizing(false);
  }
}

export async function submitAutomation(
  actions: ICompasAIChatAction[],
  data: FormValues,
  documentId: string,
  chatMessagesChainMutation: ReturnType<typeof useChatMessagesChain>,
  queryClient: ReturnType<typeof useQueryClient>,
  onClose: () => void
) {
  if (actions.length === 0 || !actions[0]) return;
  const messageId = actions[0].message_id;
  if (messageId == null) {
    toast.error("Message ID missing from actions");
    return;
  }
  try {
    await chatMessagesChainMutation.mutateAsync({
      message_id: messageId,
      context_id: documentId,
      type: "discuss",
      category_id: data.categoryId ?? null,
      groups: data.groups,
    });
    queryClient.invalidateQueries({ queryKey: ["requests"] });
    toast.success("1-Click Automation created successfully");
    onClose();
  } catch (error) {
    console.error("Failed to create automation:", error);
    toast.error("Failed to create 1-Click Automation");
  }
}
